import { api, ApiError } from "../api-client.js";
import { getCurrentUser } from "../session.js";

interface AccountStatus {
  username: string;
  jatem_user_id: string;
  secret_revealed: boolean;
  last_synced_at: string | null;
  status: string;
}

function fmtSyncedAt(iso: string | null): string {
  if (!iso) return "동기화 이력 없음";
  return new Date(iso).toLocaleString("ko-KR");
}

// 002 §7 화면6(계정/연동상태) + §8-5(연동비밀키 1회 노출). 재발급 절차는 004 §8-5에서
// Phase2 상용 공개 전까지 확정 예정이라 이번 화면엔 재발급 버튼을 넣지 않는다.
//
// 04-일반사용자-계정-추가-설계서.md §3-1/§5-4: role=USER는 상태·연동비밀키 카드를 볼 수 없다
// (백엔드도 /api/account/status 등을 ADMIN 전용으로 403 처리 — 여기서 굳이 호출하지 않는다).
// USER에게는 비밀번호 변경 카드만 남기고 제목도 그에 맞게 바꾼다.
export async function renderAccount(container: HTMLElement): Promise<void> {
  const isAdmin = getCurrentUser()?.role === "ADMIN";
  container.innerHTML = `<h2 class="page-title">${isAdmin ? "계정·연동상태" : "비밀번호 변경"}</h2>`;

  if (isAdmin) {
    let status: AccountStatus;
    try {
      status = await api.get<AccountStatus>("/api/account/status");
    } catch {
      container.innerHTML += `<p>정보를 불러오지 못했습니다.</p>`;
      return;
    }

    const infoCard = document.createElement("div");
    infoCard.className = "card";
    infoCard.innerHTML = `
      <div class="kv-list">
        <div class="kv-row"><span class="kv-label">아이디</span><span>${status.username}</span></div>
        <div class="kv-row"><span class="kv-label">JaTem 사용자ID</span><span>${status.jatem_user_id}</span></div>
        <div class="kv-row"><span class="kv-label">계정 상태</span><span>${status.status === "ACTIVE" ? "정상" : "정지됨"}</span></div>
        <div class="kv-row"><span class="kv-label">마지막 동기화</span><span>${fmtSyncedAt(status.last_synced_at)}</span></div>
      </div>
    `;
    container.appendChild(infoCard);

    // 설치 안내 / 연동비밀키
    const secretCard = document.createElement("div");
    secretCard.className = "card section-gap";
    if (status.secret_revealed) {
      secretCard.innerHTML = `
        <div class="stat-label">연동비밀키</div>
        <p style="color:var(--text-dim);font-size:14px">이미 확인하셨습니다 — 보안을 위해 다시 표시되지 않습니다.
        JaTem 외부연동 설치 시 이미 입력하셨다면 별도 조치가 필요 없습니다.</p>
      `;
    } else {
      secretCard.innerHTML = `
        <div class="stat-label">설치 안내 — 연동비밀키</div>
        <p style="color:var(--text-dim);font-size:14px">JaTem 외부연동 설치 시 필요한 연동비밀키를 아래 버튼으로 확인할 수 있습니다.
        <strong style="color:var(--gold)">이 값은 지금 한 번만 표시되며, 이후에는 다시 확인할 수 없습니다.</strong></p>
        <button class="btn-primary" id="reveal-btn" style="width:auto;padding:8px 20px;margin-top:8px">연동비밀키 확인</button>
        <div id="secret-output"></div>
      `;
    }
    container.appendChild(secretCard);

    const revealBtn = secretCard.querySelector<HTMLButtonElement>("#reveal-btn");
    revealBtn?.addEventListener("click", async () => {
      revealBtn.disabled = true;
      try {
        const { secret } = await api.get<{ secret: string }>("/api/account/secret/reveal");
        const out = secretCard.querySelector<HTMLDivElement>("#secret-output")!;
        out.innerHTML = `<div class="secret-box">${secret}</div>`;
        revealBtn.remove();
      } catch (err) {
        if (err instanceof ApiError && err.status === 410) {
          revealBtn.textContent = "이미 확인함";
        } else {
          revealBtn.disabled = false;
          revealBtn.textContent = "다시 시도";
        }
      }
    });
  }

  // 비밀번호 변경 — role 무관 노출
  const pwCard = document.createElement("div");
  pwCard.className = "card section-gap";
  pwCard.innerHTML = `
    <div class="stat-label">비밀번호 변경</div>
    <form id="pw-form" style="margin-top:12px">
      <div class="field">
        <label for="currentPassword">현재 비밀번호</label>
        <input id="currentPassword" name="currentPassword" type="password" autocomplete="current-password" required />
      </div>
      <div class="field">
        <label for="newPassword">새 비밀번호 (8자 이상)</label>
        <input id="newPassword" name="newPassword" type="password" autocomplete="new-password" minlength="8" required />
      </div>
      <button type="submit" class="btn-secondary" id="pw-submit">변경</button>
      <div class="error-text" id="pw-error" style="display:none"></div>
      <div class="hint-text" id="pw-success" style="display:none;color:var(--gold)">비밀번호가 변경되었습니다.</div>
    </form>
  `;
  container.appendChild(pwCard);

  const pwForm = pwCard.querySelector<HTMLFormElement>("#pw-form")!;
  const pwError = pwCard.querySelector<HTMLDivElement>("#pw-error")!;
  const pwSuccess = pwCard.querySelector<HTMLDivElement>("#pw-success")!;
  pwForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    pwError.style.display = "none";
    pwSuccess.style.display = "none";
    const data = new FormData(pwForm);
    try {
      await api.post("/api/account/password", {
        currentPassword: String(data.get("currentPassword") ?? ""),
        newPassword: String(data.get("newPassword") ?? ""),
      });
      pwSuccess.style.display = "block";
      pwForm.reset();
    } catch (err) {
      pwError.textContent =
        err instanceof ApiError && err.status === 401 ? "현재 비밀번호가 올바르지 않습니다." : "변경 중 오류가 발생했습니다.";
      pwError.style.display = "block";
    }
  });
}
